import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../../context/AuthContext';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Plus, Users, Search, Edit, Trash2, X, Loader2, Building, Phone, Mail } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VENDOR_CATEGORIES = [
  'Cement', 'Steel', 'Electrical', 'Plumbing', 'Paint', 'Hardware', 
  'Tiles & Flooring', 'Sanitary', 'Woodwork', 'Glass & Aluminum', 'Other'
];

const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Goa',
  'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala',
  'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland',
  'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
  'Uttar Pradesh', 'Uttarakhand', 'West Bengal', 'Delhi', 'Jammu and Kashmir',
  'Ladakh', 'Puducherry', 'Chandigarh', 'Dadra and Nagar Haveli', 'Daman and Diu',
  'Lakshadweep', 'Andaman and Nicobar Islands'
];

const VendorManagement = () => {
  const { getAuthHeader } = useAuth();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    contact_person: '',
    email: '',
    phone: '',
    category: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    gst_number: '',
    pan_number: '',
    tan_number: '',
    bank_name: '',
    bank_account_number: '',
    bank_ifsc_code: '',
    bank_branch: '',
    payment_terms: '30 days',
    credit_limit: ''
  });
  const [formErrors, setFormErrors] = useState({});

  const fetchVendors = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/erp/procurement/vendors`, {
        headers: getAuthHeader()
      });
      setVendors(res.data);
    } catch (error) {
      console.error('Error fetching vendors:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchVendors();
  }, [fetchVendors]);

  const resetForm = () => {
    setFormData({
      name: '',
      contact_person: '',
      email: '',
      phone: '',
      category: '',
      address: '',
      city: '',
      state: '',
      pincode: '',
      gst_number: '',
      pan_number: '',
      tan_number: '',
      bank_name: '',
      bank_account_number: '',
      bank_ifsc_code: '',
      bank_branch: '',
      payment_terms: '30 days',
      credit_limit: ''
    });
    setFormErrors({});
    setEditingVendor(null);
  };

  const handleOpenModal = (vendor = null) => {
    if (vendor) {
      setEditingVendor(vendor);
      setFormData({
        name: vendor.name || '',
        contact_person: vendor.contact_person || '',
        email: vendor.email || '',
        phone: vendor.phone || '',
        category: vendor.category || '',
        address: vendor.address || '',
        city: vendor.city || '',
        state: vendor.state || '',
        pincode: vendor.pincode || '',
        gst_number: vendor.gst_number || '',
        pan_number: vendor.pan_number || '',
        tan_number: vendor.tan_number || '',
        bank_name: vendor.bank_name || '',
        bank_account_number: vendor.bank_account_number || '',
        bank_ifsc_code: vendor.bank_ifsc_code || '',
        bank_branch: vendor.bank_branch || '',
        payment_terms: vendor.payment_terms || '30 days',
        credit_limit: vendor.credit_limit || ''
      });
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) errors.name = 'Vendor name is required';
    if (!formData.contact_person.trim()) errors.contact_person = 'Contact person is required';
    if (!formData.email.trim()) errors.email = 'Email is required';
    if (!/^\S+@\S+\.\S+$/.test(formData.email)) errors.email = 'Invalid email format';
    if (!formData.phone.trim()) errors.phone = 'Phone is required';
    if (!/^[0-9]{10}$/.test(formData.phone.replace(/\s/g, ''))) errors.phone = 'Phone must be 10 digits';
    if (!formData.category) errors.category = 'Category is required';
    if (!formData.address.trim()) errors.address = 'Address is required';
    if (!formData.city.trim()) errors.city = 'City is required';
    if (!formData.state) errors.state = 'State is required';
    if (!formData.pincode.trim()) errors.pincode = 'Pincode is required';
    if (!/^[0-9]{6}$/.test(formData.pincode)) errors.pincode = 'Pincode must be 6 digits';
    
    // GST validation (optional but if provided must be valid)
    if (formData.gst_number && formData.gst_number.length !== 15) {
      errors.gst_number = 'GST number must be 15 characters';
    }
    
    // PAN validation
    if (formData.pan_number && formData.pan_number.length !== 10) {
      errors.pan_number = 'PAN must be 10 characters';
    }
    
    // IFSC validation
    if (formData.bank_ifsc_code && formData.bank_ifsc_code.length !== 11) {
      errors.bank_ifsc_code = 'IFSC code must be 11 characters';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    try {
      const payload = {
        ...formData,
        credit_limit: formData.credit_limit ? parseFloat(formData.credit_limit) : null
      };
      
      if (editingVendor) {
        await axios.put(
          `${BACKEND_URL}/api/erp/procurement/vendors/${editingVendor.id}`,
          payload,
          { headers: getAuthHeader() }
        );
      } else {
        await axios.post(
          `${BACKEND_URL}/api/erp/procurement/vendors`,
          payload,
          { headers: getAuthHeader() }
        );
      }
      
      fetchVendors();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving vendor:', error);
      if (error.response?.data?.detail) {
        alert(error.response.data.detail);
      }
    }
  };

  const handleDeactivate = async (vendorId) => {
    if (!window.confirm('Are you sure you want to deactivate this vendor?')) return;
    
    try {
      await axios.delete(
        `${BACKEND_URL}/api/erp/procurement/vendors/${vendorId}`,
        { headers: getAuthHeader() }
      );
      fetchVendors();
    } catch (error) {
      console.error('Error deactivating vendor:', error);
    }
  };

  const filteredVendors = vendors.filter(v => {
    const matchesSearch = 
      v.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      v.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      v.gst_number?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesCategory = !selectedCategory || v.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Vendor Management</h1>
          <p className="text-gray-600 mt-1">Manage suppliers and vendor information</p>
        </div>
        <Button onClick={() => handleOpenModal()} className="bg-primary">
          <Plus className="w-4 h-4 mr-2" />
          Add Vendor
        </Button>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-2.5 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search by name, email, or GST number..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">All Categories</option>
              {VENDOR_CATEGORIES.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Vendors Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredVendors.map(vendor => (
          <Card key={vendor.id} className="hover:shadow-lg transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center font-bold text-lg">
                    {vendor.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <h3 className="font-bold text-lg">{vendor.name}</h3>
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                      {vendor.category}
                    </span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="ghost" onClick={() => handleOpenModal(vendor)}>
                    <Edit size={16} className="text-blue-600" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => handleDeactivate(vendor.id)}>
                    <Trash2 size={16} className="text-red-600" />
                  </Button>
                </div>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-gray-600">
                  <Users size={14} />
                  <span>{vendor.contact_person}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Mail size={14} />
                  <span className="truncate">{vendor.email}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Phone size={14} />
                  <span>{vendor.phone}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Building size={14} />
                  <span>{vendor.city}, {vendor.state}</span>
                </div>
              </div>

              {vendor.gst_number && (
                <div className="mt-3 pt-3 border-t">
                  <p className="text-xs text-gray-500">GST: {vendor.gst_number}</p>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredVendors.length === 0 && (
        <Card className="p-12 text-center">
          <Users size={48} className="mx-auto mb-4 text-gray-300" />
          <p className="text-gray-500">No vendors found</p>
          <Button onClick={() => handleOpenModal()} className="mt-4 bg-primary">
            <Plus className="w-4 h-4 mr-2" />
            Add Your First Vendor
          </Button>
        </Card>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h2 className="text-2xl font-bold">
                {editingVendor ? 'Edit Vendor' : 'Add New Vendor'}
              </h2>
              <Button size="sm" variant="ghost" onClick={handleCloseModal}>
                <X size={20} />
              </Button>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              {/* Basic Information */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-4">Basic Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Vendor Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className={`w-full px-3 py-2 border rounded-lg ${formErrors.name ? 'border-red-500' : ''}`}
                    />
                    {formErrors.name && <p className="text-xs text-red-500 mt-1">{formErrors.name}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Contact Person <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.contact_person}
                      onChange={(e) => setFormData({...formData, contact_person: e.target.value})}
                      className={`w-full px-3 py-2 border rounded-lg ${formErrors.contact_person ? 'border-red-500' : ''}`}
                    />
                    {formErrors.contact_person && <p className="text-xs text-red-500 mt-1">{formErrors.contact_person}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className={`w-full px-3 py-2 border rounded-lg ${formErrors.email ? 'border-red-500' : ''}`}
                    />
                    {formErrors.email && <p className="text-xs text-red-500 mt-1">{formErrors.email}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Phone <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className={`w-full px-3 py-2 border rounded-lg ${formErrors.phone ? 'border-red-500' : ''}`}
                      placeholder="10-digit mobile number"
                    />
                    {formErrors.phone && <p className="text-xs text-red-500 mt-1">{formErrors.phone}</p>}
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-1">
                      Category <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({...formData, category: e.target.value})}
                      className={`w-full px-3 py-2 border rounded-lg ${formErrors.category ? 'border-red-500' : ''}`}
                    >
                      <option value="">Select Category</option>
                      {VENDOR_CATEGORIES.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                    {formErrors.category && <p className="text-xs text-red-500 mt-1">{formErrors.category}</p>}
                  </div>
                </div>
              </div>

              {/* Address Information */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-4">Address Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-1">
                      Address <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={formData.address}
                      onChange={(e) => setFormData({...formData, address: e.target.value})}
                      className={`w-full px-3 py-2 border rounded-lg ${formErrors.address ? 'border-red-500' : ''}`}
                      rows="2"
                    />
                    {formErrors.address && <p className="text-xs text-red-500 mt-1">{formErrors.address}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">
                      City <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.city}
                      onChange={(e) => setFormData({...formData, city: e.target.value})}
                      className={`w-full px-3 py-2 border rounded-lg ${formErrors.city ? 'border-red-500' : ''}`}
                    />
                    {formErrors.city && <p className="text-xs text-red-500 mt-1">{formErrors.city}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">
                      State <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.state}
                      onChange={(e) => setFormData({...formData, state: e.target.value})}
                      className={`w-full px-3 py-2 border rounded-lg ${formErrors.state ? 'border-red-500' : ''}`}
                    >
                      <option value="">Select State</option>
                      {INDIAN_STATES.map(state => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </select>
                    {formErrors.state && <p className="text-xs text-red-500 mt-1">{formErrors.state}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Pincode <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.pincode}
                      onChange={(e) => setFormData({...formData, pincode: e.target.value})}
                      className={`w-full px-3 py-2 border rounded-lg ${formErrors.pincode ? 'border-red-500' : ''}`}
                      placeholder="6-digit pincode"
                      maxLength="6"
                    />
                    {formErrors.pincode && <p className="text-xs text-red-500 mt-1">{formErrors.pincode}</p>}
                  </div>
                </div>
              </div>

              {/* Tax & Compliance */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-4">Tax & Compliance Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">GST Number</label>
                    <input
                      type="text"
                      value={formData.gst_number}
                      onChange={(e) => setFormData({...formData, gst_number: e.target.value.toUpperCase()})}
                      className={`w-full px-3 py-2 border rounded-lg ${formErrors.gst_number ? 'border-red-500' : ''}`}
                      placeholder="22AAAAA0000A1Z5"
                      maxLength="15"
                    />
                    {formErrors.gst_number && <p className="text-xs text-red-500 mt-1">{formErrors.gst_number}</p>}
                    <p className="text-xs text-gray-500 mt-1">15 characters</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">PAN Number</label>
                    <input
                      type="text"
                      value={formData.pan_number}
                      onChange={(e) => setFormData({...formData, pan_number: e.target.value.toUpperCase()})}
                      className={`w-full px-3 py-2 border rounded-lg ${formErrors.pan_number ? 'border-red-500' : ''}`}
                      placeholder="AAAAA0000A"
                      maxLength="10"
                    />
                    {formErrors.pan_number && <p className="text-xs text-red-500 mt-1">{formErrors.pan_number}</p>}
                    <p className="text-xs text-gray-500 mt-1">10 characters</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">TAN Number</label>
                    <input
                      type="text"
                      value={formData.tan_number}
                      onChange={(e) => setFormData({...formData, tan_number: e.target.value.toUpperCase()})}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="AAAA00000A"
                      maxLength="10"
                    />
                    <p className="text-xs text-gray-500 mt-1">10 characters</p>
                  </div>
                </div>
              </div>

              {/* Banking Details */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-4">Banking Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Bank Name</label>
                    <input
                      type="text"
                      value={formData.bank_name}
                      onChange={(e) => setFormData({...formData, bank_name: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="e.g., State Bank of India"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Branch</label>
                    <input
                      type="text"
                      value={formData.bank_branch}
                      onChange={(e) => setFormData({...formData, bank_branch: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Account Number</label>
                    <input
                      type="text"
                      value={formData.bank_account_number}
                      onChange={(e) => setFormData({...formData, bank_account_number: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">IFSC Code</label>
                    <input
                      type="text"
                      value={formData.bank_ifsc_code}
                      onChange={(e) => setFormData({...formData, bank_ifsc_code: e.target.value.toUpperCase()})}
                      className={`w-full px-3 py-2 border rounded-lg ${formErrors.bank_ifsc_code ? 'border-red-500' : ''}`}
                      placeholder="SBIN0001234"
                      maxLength="11"
                    />
                    {formErrors.bank_ifsc_code && <p className="text-xs text-red-500 mt-1">{formErrors.bank_ifsc_code}</p>}
                    <p className="text-xs text-gray-500 mt-1">11 characters</p>
                  </div>
                </div>
              </div>

              {/* Payment Terms */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-4">Payment Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Payment Terms</label>
                    <select
                      value={formData.payment_terms}
                      onChange={(e) => setFormData({...formData, payment_terms: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                    >
                      <option value="Immediate">Immediate</option>
                      <option value="15 days">15 Days</option>
                      <option value="30 days">30 Days</option>
                      <option value="60 days">60 Days</option>
                      <option value="90 days">90 Days</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Credit Limit (₹)</label>
                    <input
                      type="number"
                      value={formData.credit_limit}
                      onChange={(e) => setFormData({...formData, credit_limit: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="Optional"
                    />
                  </div>
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={handleCloseModal}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-primary">
                  {editingVendor ? 'Update Vendor' : 'Create Vendor'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorManagement;
